// Started from shadcn/ui (new-york-v4) "field", restyled to Union (design system artifact:
// components/Field). One answer per field: label above, help below, and an error that says the
// fix ("Indexes top out at 54.0."), shown once the member has left the field.
//
//   <Field data-invalid={!!error}>
//     <FieldLabel htmlFor="guest">Guest’s name</FieldLabel>
//     <Input id="guest" aria-invalid={!!error} aria-describedby="guest-help" />
//     {error ? <FieldError>{error}</FieldError> : <FieldDescription id="guest-help">…</FieldDescription>}
//   </Field>

import { Label } from "@union/ui/components/label";
import { cn } from "@union/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { CircleAlert } from "lucide-react";
import type * as React from "react";

/** A group of related fields with a legend ("Play options"). */
function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return <fieldset data-slot="field-set" className={cn("flex flex-col gap-space-4", className)} {...props} />;
}

function FieldLegend({ className, ...props }: React.ComponentProps<"legend">) {
  return <legend data-slot="field-legend" className={cn("mb-space-2 type-title", className)} {...props} />;
}

/** Stacks fields with the standard gap. */
function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="field-group" className={cn("flex w-full flex-col gap-space-5", className)} {...props} />
  );
}

const fieldVariants = cva("group/field flex w-full gap-space-2", {
  variants: {
    orientation: {
      /** Label above the control (the default). */
      vertical: "flex-col",
      /** Checkbox or switch beside its label. */
      horizontal: "flex-row items-start [&>[role=checkbox]]:mt-0.5",
    },
  },
  defaultVariants: { orientation: "vertical" },
});

function Field({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof fieldVariants>) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: one field isn't a fieldset (that needs a legend); FieldSet groups fields
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

/** Label and description beside a horizontal control. */
function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="field-content" className={cn("flex flex-1 flex-col gap-space-1", className)} {...props} />
  );
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn("w-fit group-data-[disabled=true]/field:text-faint-foreground", className)}
      {...props}
    />
  );
}

/** Help: what the answer is for or what happens with it. */
function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn("type-caption text-muted-foreground [&>a]:text-primary-text [&>a]:underline", className)}
      {...props}
    />
  );
}

/** The fix, in place of the description. Announced to screen readers. */
function FieldError({ className, children, ...props }: React.ComponentProps<"div">) {
  if (!children) return null;
  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn("flex items-start gap-space-1 type-caption text-destructive", className)}
      {...props}
    >
      <CircleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" strokeWidth={2} />
      <span>{children}</span>
    </div>
  );
}

export { Field, FieldContent, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet };
